import db from '../db/index';
import { handleUnauthorizedResponse } from './authSession';

const API_BASE_URL = '/api';

function getAuthHeaders() {
  const headers = {
    'Content-Type': 'application/json',
  };
  const token = localStorage.getItem('gaza_pharmacy_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

class SyncService {
  constructor() {
    this.isOnline = navigator.onLine;
    this.isSyncing = false;
    this.pendingCount = 0;
    this.lastSyncedAt = null;
    this.syncStatus = 'idle';
    this.listeners = new Set();
    this.checkInterval = null;
    this.connectivityCheck = null;
    this.idMapping = new Map(); // Map temporary local IDs to server IDs
    this.initialized = false;
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.updatePendingCount();

    window.addEventListener('online', () => {
      this.checkConnectivityAndSync();
    });

    window.addEventListener('offline', () => {
      this.setOnline(false);
    });

    // Heartbeat check every 20 seconds
    this.checkInterval = setInterval(() => {
      this.checkConnectivity();
    }, 20000);

    // Initial check and pull sync on start
    this.checkConnectivityAndSync();
  }

  subscribe(callback) {
    this.listeners.add(callback);
    callback(this.getState());
    return () => this.listeners.delete(callback);
  }

  notify() {
    const state = this.getState();
    this.listeners.forEach((cb) => {
      try {
        cb(state);
      } catch (err) {
        console.error('Error notifying sync listener:', err);
      }
    });
  }

  setOnline(isOnline) {
    if (this.isOnline === isOnline) return;
    this.isOnline = isOnline;
    this.notify();
  }

  // A failed business request can be caused by one route timing out or
  // returning an error; only the health endpoint decides connectivity.
  handleRequestFailure() {
    if (!navigator.onLine) {
      this.setOnline(false);
      return;
    }
    void this.checkConnectivity();
  }

  getState() {
    return {
      isOnline: this.isOnline,
      isSyncing: this.isSyncing,
      pendingCount: this.pendingCount,
      lastSyncedAt: this.lastSyncedAt,
      syncStatus: this.syncStatus,
    };
  }

  async getPendingItems() {
    return db.syncQueue.where('synced').equals(0).sortBy('created_at');
  }

  async updatePendingCount() {
    try {
      const count = await db.syncQueue.where('synced').equals(0).count();
      this.pendingCount = count;
      this.notify();
    } catch {
      // Table might still be initializing
    }
  }

  async checkConnectivity() {
    if (!navigator.onLine) {
      this.setOnline(false);
      return false;
    }

    // Reusing one probe prevents a late timeout from an older probe from
    // overwriting a newer successful result and making the banner flicker.
    if (this.connectivityCheck) return this.connectivityCheck;

    const probe = (async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 6000);
      try {
        const res = await fetch(`${API_BASE_URL}/health`, {
          method: 'GET',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        this.setOnline(res.ok);
        return res.ok;
      } catch {
        this.setOnline(false);
        return false;
      } finally {
        clearTimeout(timeoutId);
      }
    })();

    this.connectivityCheck = probe;
    try {
      return await probe;
    } finally {
      if (this.connectivityCheck === probe) {
        this.connectivityCheck = null;
      }
    }
  }

  async checkConnectivityAndSync() {
    if (!localStorage.getItem('gaza_pharmacy_token')) return;

    const online = await this.checkConnectivity();
    if (online) {
      this.syncAll();
    }
  }

  async syncAll() {
    if (this.isSyncing) return;
    if (!localStorage.getItem('gaza_pharmacy_token')) return;
    if (!this.isOnline) {
      await this.checkConnectivity();
      if (!this.isOnline) return;
    }

    this.isSyncing = true;
    this.syncStatus = 'syncing';
    this.notify();

    try {
      // Upload offline work before replacing the local mirror. This prevents a
      // clean pull from removing records that have not reached the server yet.
      await this.pushToServer();

      if (await db.syncQueue.where('synced').equals(0).count()) {
        throw new Error('Some offline changes could not be uploaded. Keeping the local copy unchanged.');
      }

      // The dashboard is calculated from IndexedDB while offline, so it must
      // be an authoritative snapshot, rather than a mix of old and new rows.
      await this.pullFromServer({ clearLocal: true });

      this.lastSyncedAt = new Date();
      this.syncStatus = 'success';
      await this.updatePendingCount();
    } catch (err) {
      console.warn('Sync failed:', err);
      this.syncStatus = 'error';
    } finally {
      this.isSyncing = false;
      this.notify();
    }
  }

  /**
   * Complete reset of local Dexie IndexedDB cache followed by fresh pull from backend.
   * Used during Database Backup Restoration to ensure local state fully matches server state.
   */
  async resetAndPullFromServer() {
    this.isSyncing = true;
    this.syncStatus = 'syncing';
    this.notify();

    try {
      // 1. Clear offline syncQueue and reset ID map so stale offline changes are not pushed
      this.idMapping.clear();
      if (db.syncQueue) {
        await db.syncQueue.clear();
      }

      // 2. Clear all local Dexie tables
      const tableNames = [
        'medicines',
        'categories',
        'batches',
        'customers',
        'suppliers',
        'sales',
        'sale_items',
        'purchases',
        'purchase_items',
        'customer_payments',
        'supplier_payments',
        'supplier_refunds',
        'expenses',
        'expense_types',
        'damages',
        'staff',
        'users',
        'customer_refunds',
        'formulations',
        'daily_closings',
        'cash_transactions',
        'staff_transactions',
      ];

      for (const t of tableNames) {
        if (db[t]) {
          try {
            await db[t].clear();
          } catch (e) {
            console.warn(`Could not clear table ${t}:`, e);
          }
        }
      }

      // 3. Pull fresh datasets from backend server
      await this.pullFromServer({ clearLocal: false });

      this.lastSyncedAt = new Date();
      this.syncStatus = 'success';
      await this.updatePendingCount();
    } catch (err) {
      console.warn('Reset and pull failed:', err);
      this.syncStatus = 'error';
      throw err;
    } finally {
      this.isSyncing = false;
      this.notify();
    }
  }

  /**
   * Pull datasets from server and update local Dexie IndexedDB
   */
  async pullFromServer(options = {}) {
    try {
      // 1. Categories
      try {
        const categoriesRes = await this.fetchServer('/categories');
        const catList = Array.isArray(categoriesRes) ? categoriesRes : (categoriesRes?.categories || []);
        if (Array.isArray(catList)) {
          await db.categories.clear();
          if (catList.length > 0) {
            await db.categories.bulkPut(catList);
          }
        }
      } catch {
        // Ignore
      }

      // 1b. Formulations
      try {
        if (db.formulations) {
          const formRes = await this.fetchServer('/formulations');
          const formList = Array.isArray(formRes) ? formRes : (formRes?.formulations || []);
          if (Array.isArray(formList)) {
            await db.formulations.clear();
            if (formList.length > 0) {
              await db.formulations.bulkPut(formList);
            }
          }
        }
      } catch {
        // Ignore
      }

      // 2. Customers
      try {
        const custList = await this.fetchAllPages('/customers?limit=500', 'customers');
        if (Array.isArray(custList)) {
          if (options.clearLocal) await db.customers.clear();
          if (custList.length > 0) {
            await db.customers.bulkPut(custList);
          }
        }
      } catch {
        // Ignore
      }

      // 3. Suppliers
      try {
        const suppList = await this.fetchAllPages('/suppliers?limit=500', 'suppliers');
        if (Array.isArray(suppList)) {
          if (options.clearLocal) await db.suppliers.clear();
          if (suppList.length > 0) {
            await db.suppliers.bulkPut(suppList);
          }
        }
      } catch {
        // Ignore
      }

      // 4. Medicines & Batches
      try {
        const medList = await this.fetchAllPages('/medicines?limit=500', 'medicines');
        if (Array.isArray(medList)) {
          if (options.clearLocal) {
            await db.medicines.clear();
            await db.batches.clear();
          }
          if (medList.length > 0) {
            const cleanMeds = medList.map((m) => {
              const { batches, ...rest } = m;
              return rest;
            });
            await db.medicines.bulkPut(cleanMeds);

            const allBatches = medList.flatMap((m) => m.batches || []);
            if (allBatches.length > 0) {
              await db.batches.bulkPut(allBatches);
            }
          }
        }
      } catch {
        // Ignore
      }

      // 4b. Batches (full sync)
      try {
        const batchList = await this.fetchAllPages('/batches?limit=500', 'data');
        if (Array.isArray(batchList) && batchList.length > 0) {
          await db.batches.bulkPut(batchList);
        }
      } catch {
        // Ignore
      }

      // 5. Customer Payments
      try {
        const paymentsRes = await this.fetchServer('/customer-payments');
        const payList = Array.isArray(paymentsRes) ? paymentsRes : (paymentsRes?.payments || []);
        if (Array.isArray(payList)) {
          if (options.clearLocal) await db.customer_payments.clear();
          if (payList.length > 0) {
            await db.customer_payments.bulkPut(payList);
          }
        }
      } catch {
        // Ignore
      }

      // 5b. Supplier Payments
      try {
        const supPayRes = await this.fetchServer('/supplier-payments');
        const supPayList = Array.isArray(supPayRes) ? supPayRes : (supPayRes?.payments || []);
        if (Array.isArray(supPayList)) {
          if (options.clearLocal) await db.supplier_payments.clear();
          if (supPayList.length > 0) {
            await db.supplier_payments.bulkPut(supPayList);
          }
        }
      } catch {
        // Ignore
      }

      // 6. Sales
      try {
        const saleList = await this.fetchAllPages('/sales?limit=500', 'sales');
        if (Array.isArray(saleList)) {
          if (options.clearLocal) await db.sales.clear();
          if (saleList.length > 0) {
            await db.sales.bulkPut(saleList);
          }
        }
      } catch {
        // Ignore
      }

      // 6b. Sale Items
      try {
        const saleItemsRes = await this.fetchServer('/sale-items');
        const saleItemList = Array.isArray(saleItemsRes) ? saleItemsRes : (saleItemsRes?.data || saleItemsRes?.items || []);
        if (Array.isArray(saleItemList)) {
          if (options.clearLocal) await db.sale_items.clear();
          if (saleItemList.length > 0) {
            await db.sale_items.bulkPut(saleItemList);
          }
        }
      } catch {
        // Ignore
      }

      // 7. Purchases
      try {
        const purList = await this.fetchAllPages('/purchases?limit=200', 'purchases');
        if (Array.isArray(purList)) {
          if (options.clearLocal) await db.purchases.clear();
          if (purList.length > 0) {
            await db.purchases.bulkPut(purList);
          }
        }
      } catch {
        // Ignore
      }

      // 8. Expenses
      try {
        const expList = await this.fetchAllPages('/expenses?limit=500', 'expenses');
        if (Array.isArray(expList)) {
          if (options.clearLocal) await db.expenses.clear();
          if (expList.length > 0) {
            await db.expenses.bulkPut(expList);
          }
        }
      } catch {
        // Ignore
      }

      // 8a. Expense Types
      try {
        const etRes = await this.fetchServer('/expense-types');
        const etList = Array.isArray(etRes) ? etRes : (etRes?.expense_types || []);
        if (Array.isArray(etList)) {
          if (options.clearLocal) await db.expense_types.clear();
          if (etList.length > 0) {
            await db.expense_types.bulkPut(etList);
          }
        }
      } catch {
        // Ignore
      }

      // 8b. Damaged Stock
      try {
        const damageRes = await this.fetchServer('/damages?limit=5000');
        const damageList = Array.isArray(damageRes) ? damageRes : (damageRes?.damages || []);
        if (Array.isArray(damageList)) {
          if (options.clearLocal) await db.damages.clear();
          if (damageList.length > 0) {
            await db.damages.bulkPut(damageList);
          }
        }
      } catch {
        // Ignore
      }

      // 8c. Customer Refunds
      try {
        if (db.customer_refunds) {
          const crRes = await this.fetchServer('/customer-refunds');
          const crList = Array.isArray(crRes) ? crRes : (crRes?.refunds || []);
          if (Array.isArray(crList)) {
            if (options.clearLocal) await db.customer_refunds.clear();
            if (crList.length > 0) {
              await db.customer_refunds.bulkPut(crList);
            }
          }
        }
      } catch {
        // Ignore
      }

      // 8d. Supplier Refunds
      try {
        if (db.supplier_refunds) {
          const srRes = await this.fetchServer('/supplier-refunds');
          const srList = Array.isArray(srRes) ? srRes : (srRes?.refunds || []);
          if (Array.isArray(srList)) {
            if (options.clearLocal) await db.supplier_refunds.clear();
            if (srList.length > 0) {
              await db.supplier_refunds.bulkPut(srList);
            }
          }
        }
      } catch {
        // Ignore
      }

      // 9. Staff
      try {
        const staffRes = await this.fetchServer('/staff');
        const staffList = Array.isArray(staffRes) ? staffRes : (staffRes?.staff || []);
        if (Array.isArray(staffList)) {
          if (options.clearLocal) await db.staff.clear();
          if (staffList.length > 0) {
            await db.staff.bulkPut(staffList);
          }
        }
      } catch {
        // Ignore
      }

      // 9b. Users
      try {
        if (db.users) {
          const usersRes = await this.fetchServer('/users?limit=500');
          const userList = Array.isArray(usersRes) ? usersRes : (usersRes?.users || []);
          if (Array.isArray(userList)) {
            if (options.clearLocal) await db.users.clear();
            if (userList.length > 0) {
              await db.users.bulkPut(userList);
            }
          }
        }
      } catch {
        // Ignore
      }

      // 10. Settings
      try {
        const settingsRes = await this.fetchServer('/settings');
        if (settingsRes && typeof settingsRes === 'object') {
          const current = JSON.parse(localStorage.getItem('gaza_pharmacy_settings') || '{}');
          localStorage.setItem('gaza_pharmacy_settings', JSON.stringify({ ...current, ...settingsRes }));
        }
      } catch {
        // Ignore
      }

      // 11. Daily Closings
      try {
        if (db.daily_closings) {
          const dcRes = await this.fetchServer('/daily-closings');
          const dcList = Array.isArray(dcRes) ? dcRes : (dcRes?.cashiers || []);
          if (Array.isArray(dcList) && dcList.length > 0) {
            const closingsToSave = dcList.filter((c) => c.status === 'CLOSED').map((c) => ({
              business_date: dcRes?.date || new Date().toISOString().slice(0, 10),
              staff_id: c.staff_id,
              staff_name: c.staff_name,
              role: c.role,
              invoice_count: c.invoice_count,
              total_sales: c.total_sales,
              cash_sales: c.cash_sales,
              card_sales: c.card_sales,
              online_sales: c.online_sales,
              credit_sales: c.credit_sales,
              closed_by: c.closed_by,
              closed_at: c.closed_at,
              status: 'CLOSED',
              notes: c.notes,
            }));
            if (closingsToSave.length > 0) {
              if (options.clearLocal) await db.daily_closings.clear();
              await db.daily_closings.bulkPut(closingsToSave);
            }
          }
        }
      } catch {
        // Ignore
      }

      // 12. Cash Transactions
      try {
        if (db.cash_transactions) {
          const ctList = await this.fetchAllPages('/cash-transactions?limit=200', 'transactions');
          if (Array.isArray(ctList)) {
            if (options.clearLocal) await db.cash_transactions.clear();
            if (ctList.length > 0) {
              await db.cash_transactions.bulkPut(ctList);
            }
          }
        }
      } catch {
        // Ignore
      }

      // 13. Staff Transactions
      try {
        if (db.staff_transactions) {
          const stList = await this.fetchAllPages('/staff-transactions?limit=500', 'transactions');
          if (Array.isArray(stList)) {
            if (options.clearLocal) await db.staff_transactions.clear();
            if (stList.length > 0) {
              await db.staff_transactions.bulkPut(stList);
            }
          }
        }
      } catch {
        // Ignore
      }
    } catch (err) {
      console.warn('Error during pullFromServer:', err);
    }
  }

  /**
   * Push queued offline mutations to backend server
   */
  async pushToServer() {
    const queue = await db.syncQueue.where('synced').equals(0).sortBy('id');
    if (queue.length === 0) return;

    for (const item of queue) {
      try {
        let endpoint = item.endpoint;
        let payload = item.payload ? JSON.parse(JSON.stringify(item.payload)) : null;

        // Replace any temporary local ID if mapped
        if (item.table_name && item.record_id && this.idMapping.has(`${item.table_name}:${item.record_id}`)) {
          const serverId = this.idMapping.get(`${item.table_name}:${item.record_id}`);
          endpoint = endpoint.replace(`/${item.record_id}`, `/${serverId}`);
        }

        // Fix relations in payload if mapped
        if (payload && payload.customer_id && this.idMapping.has(`customers:${payload.customer_id}`)) {
          payload.customer_id = this.idMapping.get(`customers:${payload.customer_id}`);
        }
        if (payload && payload.supplier_id && this.idMapping.has(`suppliers:${payload.supplier_id}`)) {
          payload.supplier_id = this.idMapping.get(`suppliers:${payload.supplier_id}`);
        }
        if (payload && payload.items && Array.isArray(payload.items)) {
          for (const it of payload.items) {
            if (it.medicine_id && this.idMapping.has(`medicines:${it.medicine_id}`)) {
              it.medicine_id = this.idMapping.get(`medicines:${it.medicine_id}`);
            }
          }
        }

        const res = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: item.method,
          headers: getAuthHeaders(),
          body: payload ? JSON.stringify(payload) : undefined,
        });

        if (res.ok) {
          const responseData = await res.json().catch(() => ({}));
          const serverRecord = responseData?.data || responseData;
          const newServerId = serverRecord?.id || serverRecord?.transaction?.id || serverRecord?.closing_id;
          const targetTable = (item.table_name || '').replace(/-/g, '_');

          // If this was a creation with a local ID and the server returned a new ID:
          if (item.method === 'POST' && item.record_id && newServerId && newServerId !== item.record_id) {
            this.idMapping.set(`${item.table_name}:${item.record_id}`, newServerId);
            this.idMapping.set(`${targetTable}:${item.record_id}`, newServerId);

            // Update record in local Dexie table with server ID
            if (targetTable && db[targetTable]) {
              try {
                const localDoc = await db[targetTable].get(item.record_id);
                if (localDoc) {
                  await db[targetTable].delete(item.record_id);
                  const serverEntity = serverRecord?.transaction || (serverRecord?.id ? serverRecord : localDoc);
                  await db[targetTable].put({ ...localDoc, ...serverEntity, id: newServerId });
                }
              } catch (e) {
                console.warn('Error updating local ID mapping:', e);
              }
            }
          }

          // If staff record state was returned (e.g. staff transaction with updated advance balance)
          if (serverRecord?.staff && db.staff) {
            try {
              const staffId = serverRecord.staff.id;
              if (staffId) {
                await db.staff.update(staffId, { advanced_payment: serverRecord.staff.advanced_payment });
              }
            } catch (e) {
              console.warn('Error updating local staff advance after sync:', e);
            }
          }

          // Mark queue item as synced & remove
          await db.syncQueue.delete(item.id);
        } else if (res.status === 401 || res.status === 403) {
          throw handleUnauthorizedResponse();
        } else if (res.status >= 400 && res.status < 500) {
          // Client error (e.g. malformed or invalid request) - remove from queue to prevent blocking
          console.warn(`Sync queue item ${item.id} skipped due to HTTP ${res.status}`);
          await db.syncQueue.delete(item.id);
        } else {
          // 5xx Server error: stop batch push to retry next cycle and preserve transaction order
          console.warn(`Sync queue item ${item.id} failed with HTTP ${res.status}, will retry next cycle`);
          break;
        }
      } catch (err) {
        // Network error: stop batch push to preserve transaction order
        console.warn(`Failed to push syncQueue item ${item.id}:`, err);
        break;
      }
    }

    await this.updatePendingCount();
  }

  async enqueue(operation, table_name, record_id, endpoint, method, payload) {
    try {
      await db.syncQueue.add({
        operation,
        table_name,
        record_id: record_id || null,
        endpoint,
        method,
        payload,
        synced: 0,
        created_at: new Date().toISOString(),
      });
      await this.updatePendingCount();
    } catch (err) {
      console.error('Failed to enqueue sync operation:', err);
    }
  }

  async fetchServer(endpoint) {
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        throw handleUnauthorizedResponse();
      }
      throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    }
    const json = await res.json();
    return json?.data || json;
  }

  /** Fetch every page of a server collection. API endpoints enforce their own
   * maximum page sizes, so a large requested limit alone is not sufficient. */
  async fetchAllPages(endpoint, collectionKey) {
    const first = await this.fetchServer(endpoint);
    const firstItems = Array.isArray(first) ? first : (first?.[collectionKey] || []);
    const pagination = first?.pagination || {};
    const totalPages = Number(pagination.total_pages ?? pagination.totalPages ?? pagination.pages ?? 1);

    if (!Number.isFinite(totalPages) || totalPages <= 1) return firstItems;

    const requested = new URLSearchParams(endpoint.includes('?') ? endpoint.slice(endpoint.indexOf('?') + 1) : '');
    const pageSize = Number(pagination.limit) || Number(requested.get('limit')) || firstItems.length || 100;
    const basePath = endpoint.split('?')[0];
    const allItems = [...firstItems];

    for (let page = 2; page <= totalPages; page += 1) {
      const params = new URLSearchParams(requested);
      params.set('page', String(page));
      params.set('limit', String(pageSize));
      const response = await this.fetchServer(`${basePath}?${params.toString()}`);
      const items = Array.isArray(response) ? response : (response?.[collectionKey] || []);
      allItems.push(...items);
    }

    return allItems;
  }
}

export const syncService = new SyncService();
export default syncService;
